/**
 * Role-refresh runner shared by every role's dashboard data hook (KAN-589,
 * Lane B). Two jobs:
 *
 * 1. HONEST OUTCOME — the CORE load (the role's own `loadData`) must make the
 *    pull report failure, but `loadData({ silent: true })` swallows its own
 *    errors on purpose (the silent RESUME path must stay quiet on screen). So
 *    each `loadData` records `loadFailedRef.current = true` in its catch, and
 *    this runner turns that into a REJECTION. The resume caller catches it.
 * 2. BEST-EFFORT EXTRAS — tiles/contexts that live outside the role hook
 *    (Recent activity, credits tile, delivery issues, catalog, company
 *    settings, seat limits…) register a refetch via `useDashboardRefreshExtra`;
 *    they run in parallel with the core load under `Promise.allSettled` and can
 *    NEVER reject the refresh.
 *
 * Module-level registry on purpose: only one role home is mounted at a time and
 * every registrant unregisters on unmount.
 */
const extras = new Map(); // key -> { fn, manualOnly }

/**
 * @param {() => any} fn
 * @param {{ key?: string, manualOnly?: boolean }} [opts] `key`: registrants with
 *   the same key collapse to ONE entry (the latest registrant's fn runs once;
 *   an older registrant's unregister never removes the newer one). `manualOnly`:
 *   skipped on silent RESUME refreshes (the owner already has its own resume read).
 */
export function registerDashboardRefreshExtra(fn, { key, manualOnly = false } = {}) {
  const mapKey = key ?? Symbol('refresh-extra');
  const entry = { fn, manualOnly };
  extras.set(mapKey, entry);
  return () => { if (extras.get(mapKey) === entry) extras.delete(mapKey); };
}

// Silent resume refreshes in flight (a resume is not a manual pull).
let resumeInFlight = 0;
/** Wraps a silent resume refresh so `manualOnly` extras skip it. */
export function runAsResume(fn) {
  resumeInFlight += 1;
  let p;
  try { p = Promise.resolve(fn()); } catch (e) { p = Promise.reject(e); }
  const done = () => { resumeInFlight -= 1; };
  p.then(done, done);
  return p;
}

// The mounted role's own refresh (core load + extras), so a non-dashboard
// SURFACE that replaces the pull handler (Credits sub-tab, Account) can chain
// it and keep the rest of its screen fresh. Set by useDashboardResumeRefresh.
let roleRefresh = null;
export function registerRoleRefresh(fn) {
  roleRefresh = fn;
  return () => { if (roleRefresh === fn) roleRefresh = null; };
}
/** Rejects when the role's CORE load failed (same contract as the pull). */
export function runRoleRefresh() {
  return roleRefresh ? Promise.resolve().then(roleRefresh) : Promise.resolve();
}

/** Runs registered + passed-in extras. Never rejects. */
export async function runDashboardRefreshExtras(additional = []) {
  const own = [...extras.values()]
    .filter((e) => !(e.manualOnly && resumeInFlight > 0))
    .map((e) => e.fn);
  const fns = [...own, ...additional].filter((fn) => typeof fn === 'function');
  const results = await Promise.allSettled(fns.map(async (fn) => fn()));
  results.forEach((r) => {
    // eslint-disable-next-line no-console
    if (r.status === 'rejected') console.warn('[refresh] best-effort step failed:', r.reason);
  });
}

/**
 * @param {{
 *   ensureFreshSession?: () => Promise<any>,
 *   loadData: (opts?: { silent?: boolean }) => Promise<any>,
 *   loadFailedRef?: { current: boolean },
 *   extras?: Array<() => any>,
 * }} deps
 * @returns {Promise<void>} rejects ONLY when the core load failed.
 */
export async function runPullRefresh({
  ensureFreshSession, loadData, loadFailedRef, extras: additional = [],
}) {
  if (ensureFreshSession) await ensureFreshSession();
  if (loadFailedRef) loadFailedRef.current = false;
  await Promise.all([
    loadData({ silent: true }),
    runDashboardRefreshExtras(additional),
  ]);
  if (loadFailedRef && loadFailedRef.current) {
    throw new Error('Dashboard refresh failed');
  }
}
